/* Canvello v8 — collection (Task 4.1). sections/cv-collection.liquid.
 *
 * The section works without this file: the rail is a GET form (Apply in
 * <noscript>), sort options, active chips, Clear all and pages are links.
 * This file loads every one of them in place:
 *
 *   URL state    each change fetches the new URL with ?section_id=<this
 *                section> (Shopify's Section Rendering API), swaps the
 *                [data-cv-part] regions and pushes the URL; Back and
 *                Forward render the URL they land on. Responses are kept
 *                for the page's life, so going back is instant.
 *   Rail         a tick filters at once; price waits for a pause in typing
 *                (or Enter). Folded groups, "Show all" groups, the rail's
 *                scroll and the focused control survive each swap.
 *   Drawer       ≤ 1040px the rail is moved into a modal <dialog> (the page
 *                behind is inert, the scroll locked, Tab wraps, Escape and
 *                the scrim close it, focus returns to Refine). Its Done
 *                button reads "Show N styles".
 *   Sort         a disclosure: ↓/↑ move through the options, Escape and a
 *                click outside close it.
 *   Density      comfort / compact grid, remembered on this device.
 *   Pages        load in place, then scroll to the grid and focus its
 *                heading.
 *   Announce     the new count is spoken (polite status) after each change.
 *   Search       on the search page (Task 4.4) a new query from the tool
 *                bar's field loads in place too, and the page title follows.
 */
(() => {
  if (customElements.get('cv-collection')) return;

  const root = document.documentElement;
  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea, [tabindex]:not([tabindex="-1"])';
  const visible = (el) => !!(el && (el.offsetWidth || el.offsetHeight || el.getClientRects().length));
  const narrow = window.matchMedia('(max-width: 1040px)');
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  const cache = new Map();
  const store = {
    get: (k) => {
      try {
        return localStorage.getItem(k);
      } catch (e) {
        return null;
      }
    },
    set: (k, v) => {
      try {
        localStorage.setItem(k, v);
      } catch (e) {}
    },
  };

  class CVCollection extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.sid = this.dataset.section;
      this.rail = this.querySelector('[data-cv-rail]');
      this.railSlot = this.querySelector('[data-cv-rail-slot]');
      this.dialog = this.querySelector('[data-cv-rail-dialog]');
      this.refine = this.querySelector('[data-cv-refine]');
      this.results = this.querySelector('[data-cv-results]');
      this.heading = this.querySelector(`#cvGridTitle-${CSS.escape(this.sid)}`);
      this.live = this.querySelector('[data-cv-live]');
      this.bar = this.querySelector('[data-cv-bar]');
      this.folded = new Set();
      this.expanded = new Set();
      this.timer = 0;
      this.ctrl = null;

      this.setAttribute('data-cv-ready', '');
      this.syncMore();
      this.setDensity(store.get('cv-density') || 'comfort', false);

      // The page as first served is a state too, for Back.
      history.replaceState(Object.assign({}, history.state, { cv: this.sid }), '', location.href);

      this.addEventListener('change', (e) => this.onChange(e));
      this.addEventListener('input', (e) => this.onInput(e));
      this.addEventListener('submit', (e) => this.onSubmit(e));
      this.addEventListener('click', (e) => this.onClick(e));
      this.addEventListener('keydown', (e) => this.onKey(e));
      this.onPop = () => this.go(location.href, { push: false });
      window.addEventListener('popstate', this.onPop);
      document.addEventListener('click', (e) => {
        if (!e.target.closest || !e.target.closest('[data-cv-sort]')) this.closeSort();
      });

      if (this.dialog) {
        this.dialog.addEventListener('cancel', (e) => {
          e.preventDefault();
          this.closeDrawer();
        });
        this.dialog.addEventListener('close', () => this.restoreRail());
        this.dialog.addEventListener('click', (e) => {
          if (e.target !== this.dialog) return;
          const r = this.dialog.getBoundingClientRect();
          if (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) this.closeDrawer();
        });
        this.dialog.addEventListener('keydown', (e) => this.trap(e, this.dialog));
        narrow.addEventListener('change', () => !narrow.matches && this.closeDrawer());
      }

      const q = this.querySelector('[data-cv-q]');
      const qclear = this.querySelector('[data-cv-qclear]');
      if (q && qclear) {
        const sync = () => (qclear.hidden = !q.value);
        q.addEventListener('input', sync);
        qclear.addEventListener('click', () => {
          q.value = '';
          sync();
          q.focus();
        });
        sync();
      }
    }

    disconnectedCallback() {
      window.removeEventListener('popstate', this.onPop);
    }

    key(url) {
      const u = new URL(url, location.origin);
      u.searchParams.delete('section_id');
      u.hash = '';
      return u.pathname + u.search;
    }

    /* ---- Requests -------------------------------------------------------- */

    form() {
      return this.querySelector('[data-cv-facets]');
    }

    formUrl() {
      const form = this.form();
      const params = new URLSearchParams();
      for (const [k, v] of new FormData(form)) if (String(v).trim() !== '') params.append(k, v);
      const qs = params.toString();
      return form.action.split('?')[0] + (qs ? '?' + qs : '');
    }

    async go(url, { push = true, page = false } = {}) {
      const key = this.key(url);
      if (this.ctrl) this.ctrl.abort();
      const ctrl = (this.ctrl = new AbortController());
      this.results.setAttribute('aria-busy', 'true');
      let html = cache.get(key);
      try {
        if (!html) {
          const u = new URL(url, location.origin);
          u.searchParams.set('section_id', this.sid);
          const r = await fetch(u.pathname + u.search, { signal: ctrl.signal, headers: { Accept: 'text/html' } });
          if (!r.ok) throw new Error(String(r.status));
          html = await r.text();
          cache.set(key, html);
        }
      } catch (err) {
        if (err.name === 'AbortError') return;
        // Fall back to a full page load: the URL works on its own.
        location.assign(url);
        return;
      }
      if (ctrl !== this.ctrl) return;
      this.ctrl = null;
      this.render(html);
      this.results.removeAttribute('aria-busy');
      if (push && this.key(location.href) !== key) history.pushState({ cv: this.sid }, '', url);
      if (page) this.toGrid();
      this.announce();
    }

    render(html) {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const active = document.activeElement;
      const focusId = active && this.contains(active) && active.id ? active.id : null;
      const focusPart = active && active.closest && active.closest('[data-cv-part]');
      const focusLink = !focusId && active && active.matches && active.matches('[data-cv-link]') ? active.getAttribute('data-cv-link') : null;
      const scroll = this.rail ? this.rail.scrollTop : 0;

      this.querySelectorAll('[data-cv-part]').forEach((el) => {
        const next = doc.querySelector(`[data-cv-part="${el.dataset.cvPart}"]`);
        if (next) el.innerHTML = next.innerHTML;
      });

      this.syncMore();
      if (this.rail) this.rail.scrollTop = scroll;
      const title = this.querySelector('[data-cv-part="title"]');
      if (title && title.textContent.trim()) document.title = title.textContent.trim();

      // Keep focus where it was: the same control by id, else a control of
      // the same kind in the region it was in, else the rail's or the
      // grid's heading.
      if (focusId) {
        const el = document.getElementById(focusId);
        if (el && visible(el)) el.focus({ preventScroll: true });
        else if (this.rail && this.rail.contains(active)) this.rail.querySelector('[data-cv-group-toggle]')?.focus({ preventScroll: true });
      } else if (focusPart && !document.contains(active)) {
        const part = this.querySelector(`[data-cv-part="${focusPart.dataset.cvPart}"]`);
        const same = part && (part.querySelector(`[data-cv-link="${focusLink}"]`) || part.querySelector(FOCUSABLE));
        if (same && visible(same)) same.focus({ preventScroll: true });
        else if (this.heading) this.heading.focus({ preventScroll: true });
      }

      const grid = this.querySelector('[data-cv-part="grid"]');
      if (window.CVMotion && grid) window.CVMotion.scan(grid);
    }

    announce() {
      const text = this.querySelector('[data-cv-part="announce"]');
      if (!this.live || !text) return;
      this.live.textContent = '';
      requestAnimationFrame(() => (this.live.textContent = text.textContent.trim()));
    }

    toGrid() {
      const top = (this.bar || this.results).getBoundingClientRect().top + window.scrollY;
      const head = parseFloat(getComputedStyle(root).getPropertyValue('--cv-header-h')) || 0;
      window.scrollTo({ top: Math.max(0, top - head - 12), behavior: reduce.matches ? 'auto' : 'smooth' });
      if (this.heading) this.heading.focus({ preventScroll: true });
    }

    /* ---- Events ---------------------------------------------------------- */

    onChange(e) {
      if (!e.target.closest('[data-cv-facets]')) return;
      if (e.target.matches('[data-cv-price]')) return; // waits for the pause
      clearTimeout(this.timer);
      this.go(this.formUrl());
    }

    onInput(e) {
      if (!e.target.matches('[data-cv-price]')) return;
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.go(this.formUrl()), 800);
    }

    onSubmit(e) {
      if (e.target.matches('[data-cv-search-form]') && this.hasAttribute('data-search')) {
        const q = e.target.querySelector('input[name="q"]');
        if (!q || !q.value.trim()) return;
        e.preventDefault();
        const params = new URLSearchParams(new FormData(e.target));
        this.go(e.target.action.split('?')[0] + '?' + params.toString());
        return;
      }
      if (!e.target.matches('[data-cv-facets]')) return;
      e.preventDefault();
      clearTimeout(this.timer);
      this.go(this.formUrl());
    }

    onClick(e) {
      const t = e.target;
      const link = t.closest('a[data-cv-link]');
      if (link && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey && e.button === 0) {
        e.preventDefault();
        const kind = link.getAttribute('data-cv-link');
        if (kind === 'sort') this.closeSort(true);
        this.go(link.href, { page: kind === 'page' });
        return;
      }
      const toggle = t.closest('[data-cv-group-toggle]');
      if (toggle) {
        const group = toggle.closest('[data-cv-group]');
        const open = toggle.getAttribute('aria-expanded') !== 'true';
        if (open) this.folded.delete(group.dataset.cvGroup);
        else this.folded.add(group.dataset.cvGroup);
        this.syncMore();
        return;
      }
      const more = t.closest('[data-cv-more]');
      if (more) {
        const group = more.closest('[data-cv-group]').dataset.cvGroup;
        if (this.expanded.has(group)) this.expanded.delete(group);
        else this.expanded.add(group);
        this.syncMore();
        return;
      }
      if (t.closest('[data-cv-refine]')) return this.openDrawer();
      if (t.closest('[data-cv-rail-close], [data-cv-done]')) return this.closeDrawer();
      const sortBtn = t.closest('[data-cv-sort-toggle]');
      if (sortBtn) return this.toggleSort();
      const dens = t.closest('[data-cv-density]');
      if (dens) this.setDensity(dens.getAttribute('data-cv-density'), true);
    }

    onKey(e) {
      // A grouped filter row (colour, size) is a link that acts as a tick
      // box: Space toggles it, as on a real one.
      if (e.key === ' ' && e.target.matches && e.target.matches('a[role="checkbox"][data-cv-link]')) {
        e.preventDefault();
        e.target.click();
        return;
      }
      const menu = this.querySelector('.cv-sort__menu');
      if (!menu || menu.hidden) return;
      const opts = Array.from(menu.querySelectorAll('a'));
      const i = opts.indexOf(document.activeElement);
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        this.closeSort(true);
      } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        const step = e.key === 'ArrowDown' ? 1 : -1;
        const next = i === -1 ? (step > 0 ? 0 : opts.length - 1) : (i + step + opts.length) % opts.length;
        opts[next].focus();
      } else if (e.key === 'Home' || e.key === 'End') {
        e.preventDefault();
        opts[e.key === 'Home' ? 0 : opts.length - 1].focus();
      } else if (e.key === 'Tab' && i !== -1) {
        // Leaving the menu closes it.
        requestAnimationFrame(() => !menu.contains(document.activeElement) && this.closeSort());
      }
    }

    /* Folded groups and "Show all", re-applied after every swap. */
    syncMore() {
      this.querySelectorAll('[data-cv-group]').forEach((g) => {
        const key = g.dataset.cvGroup;
        const toggle = g.querySelector('[data-cv-group-toggle]');
        const body = g.querySelector('.cv-facets__body');
        const folded = this.folded.has(key);
        if (toggle) toggle.setAttribute('aria-expanded', String(!folded));
        if (body) body.hidden = folded;
        const more = g.querySelector('[data-cv-more]');
        const open = this.expanded.has(key);
        g.toggleAttribute('data-expanded', open);
        if (more) {
          more.hidden = false;
          more.setAttribute('aria-expanded', String(open));
          more.textContent = open ? more.dataset.labelLess : more.dataset.labelMore;
        }
      });
    }

    /* ---- Sort ------------------------------------------------------------ */

    toggleSort() {
      const menu = this.querySelector('.cv-sort__menu');
      if (menu.hidden) {
        menu.hidden = false;
        this.querySelector('[data-cv-sort-toggle]').setAttribute('aria-expanded', 'true');
        const cur = menu.querySelector('[aria-current]') || menu.querySelector('a');
        if (cur) cur.focus();
      } else this.closeSort(true);
    }

    closeSort(refocus = false) {
      const menu = this.querySelector('.cv-sort__menu');
      if (!menu || menu.hidden) return;
      menu.hidden = true;
      const btn = this.querySelector('[data-cv-sort-toggle]');
      btn.setAttribute('aria-expanded', 'false');
      if (refocus) btn.focus();
    }

    /* ---- Density --------------------------------------------------------- */

    setDensity(mode, remember) {
      if (mode !== 'compact') mode = 'comfort';
      this.results.setAttribute('data-density', mode);
      this.querySelectorAll('[data-cv-density]').forEach((b) => b.setAttribute('aria-pressed', String(b.getAttribute('data-cv-density') === mode)));
      if (remember) store.set('cv-density', mode);
    }

    /* ---- Drawer ---------------------------------------------------------- */

    openDrawer() {
      if (!this.dialog || !this.rail || this.dialog.open) return;
      this.dialog.append(this.rail);
      this.dialog.showModal();
      root.setAttribute('data-cv-locked', '');
      this.refine.setAttribute('aria-expanded', 'true');
      this.rail.scrollTop = 0;
      const close = this.rail.querySelector('[data-cv-rail-close]');
      if (close) close.focus();
    }

    closeDrawer() {
      if (this.dialog && this.dialog.open) this.dialog.close();
    }

    restoreRail() {
      if (this.rail && this.railSlot && this.rail.parentNode !== this.railSlot) this.railSlot.append(this.rail);
      root.removeAttribute('data-cv-locked');
      if (this.refine) {
        this.refine.setAttribute('aria-expanded', 'false');
        if (visible(this.refine)) this.refine.focus();
      }
    }

    trap(e, box) {
      if (e.key !== 'Tab') return;
      const items = Array.from(box.querySelectorAll(FOCUSABLE)).filter(visible);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && (document.activeElement === first || !box.contains(document.activeElement))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (document.activeElement === last || !box.contains(document.activeElement))) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  customElements.define('cv-collection', CVCollection);
})();
